import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import {
  getTeacherCohortQuiz,
  getTeacherQuiz,
  type TeacherQuizRecord,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { QuizRichText } from "../components/QuizRichText";
import { QuizIcon } from "../teacher/QuizIcon";

export default function TeacherQuizPreviewPage() {
  const { courseId, cohortId, quizId } = useParams<{
    courseId?: string;
    cohortId?: string;
    quizId: string;
  }>();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo")?.trim() || "";
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [quiz, setQuiz] = useState<TeacherQuizRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!bearer || !quizId) return;
    setLoading(true);
    setError(null);
    try {
      if (cohortId) {
        const { quiz: q } = await getTeacherCohortQuiz(bearer, cohortId, quizId);
        setQuiz(q);
      } else if (courseId) {
        const { quiz: q } = await getTeacherQuiz(bearer, courseId, quizId);
        setQuiz(q);
      } else {
        throw new Error("Missing course or cohort context");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load quiz");
    } finally {
      setLoading(false);
    }
  }, [bearer, cohortId, courseId, quizId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!quizId || (!courseId && !cohortId)) return <p>Missing course or cohort context.</p>;
  if (loading) return <p className="teacher-pad">Loading preview…</p>;
  if (!quiz) return <p className="teacher-pad">{error ?? "Quiz not found"}</p>;

  const resolvedCourseId = courseId ?? quiz.courseId ?? "";
  const editUrl = cohortId
    ? undefined
    : resolvedCourseId
      ? `/teacher/courses/${encodeURIComponent(resolvedCourseId)}/quizzes/${encodeURIComponent(quizId)}`
      : undefined;
  const backUrl =
    returnTo && returnTo.startsWith("/")
      ? returnTo
      : cohortId
        ? `/teacher/cohorts/${encodeURIComponent(cohortId)}`
        : resolvedCourseId
          ? `/teacher/courses/${encodeURIComponent(resolvedCourseId)}/manage`
          : "/teacher";

  return (
    <div className="teacher-quiz-preview teacher-pad">
      <header className="teacher-quiz-editor-head">
        <QuizIcon
          quizId={quiz.id}
          title={quiz.title}
          iconName={quiz.iconName}
          bgColor={quiz.iconBgColor}
          fgColor={quiz.iconFgColor}
        />
        <div className="teacher-quiz-editor-head-copy">
          <p className="teacher-quiz-preview-badge">Teacher review</p>
          <h1 className="teacher-panel-title">{quiz.title}</h1>
          {quiz.description?.trim() ? (
            <p className="teacher-panel-lede">{quiz.description}</p>
          ) : null}
          <p className="muted small">
            Full question pool ({quiz.questions.length} questions). Students see{" "}
            {quiz.displayQuestionCount} per attempt without correct answers.
            {cohortId ? " This is the cohort copy of the quiz." : null}
          </p>
        </div>
      </header>

      <div className="teacher-quiz-preview-actions">
        {editUrl ? (
          <Link className="btn secondary" to={editUrl}>
            Edit quiz
          </Link>
        ) : null}
        <Link className="btn ghost" to={backUrl}>
          Back
        </Link>
      </div>

      <div className="teacher-quiz-questions">
        {quiz.questions.map((q, qi) => (
          <article key={q.id} className="teacher-quiz-question-card teacher-quiz-preview-card">
            <h3 className="teacher-quiz-question-heading">Question {qi + 1}</h3>
            <div className="teacher-quiz-preview-prompt">
              <QuizRichText text={q.prompt} />
            </div>
            <ul className="teacher-quiz-preview-options">
              {q.options.map((opt, oi) => (
                <li
                  key={oi}
                  className={`teacher-quiz-preview-option${q.correctOptionIndex === oi ? " is-correct" : ""}`}
                >
                  {q.correctOptionIndex === oi ? (
                    <FontAwesomeIcon icon={faCheck} className="teacher-quiz-preview-check" aria-hidden />
                  ) : (
                    <span className="teacher-quiz-preview-check-placeholder" aria-hidden />
                  )}
                  <QuizRichText text={opt} />
                </li>
              ))}
            </ul>
            {q.rationale?.trim() ? (
              <p className="teacher-quiz-preview-rationale">
                <strong>Why this question:</strong> {q.rationale}
              </p>
            ) : null}
            {q.optionExplanations?.some((x) => x.trim()) ? (
              <ul className="teacher-quiz-preview-explanations">
                {q.options.map((opt, oi) => {
                  const note = q.optionExplanations?.[oi]?.trim();
                  if (!note) return null;
                  return (
                    <li key={oi}>
                      <strong>{opt || `Option ${oi + 1}`}:</strong> {note}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </article>
        ))}
      </div>
    </div>
  );
}
